import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

import { setAuth } from '../../redux/infoSlice';
import { authorizeAPI, userAPI, settingAPI } from '../../actions/imsServer';

import { DidimLogo } from '../../assets/images/didimLogo';

// Import our custom InputField component
import InputField from '../formItem/inputField';

import Button from '../button';

// Import our custom SocialLoginSection component
import SocialLoginProvider from './components/socialLoginProvider';

// Import our custom SignUpLink component
import SignUpLink from './components/signUpLink';

// Import our common StyledContainer component
import StyledContainer from './components/styledContainer';

import { isWebView } from '../../utils/base';
import { API_PAGE_SIZE } from '../../constants';
import FormLabel from '../formItem/formLabel';
import { isEmail } from '../../utils/password-validation';
import * as jwt from '../../actions/jwt';

export const SignIn = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const theme = useTheme();

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [emailError, setEmailError] = useState(false);
    const [emailErrorMessage, setEmailErrorMessage] = useState('');
    const [passwordError, setPasswordError] = useState(false);
    const [passwordErrorMessage, setPasswordErrorMessage] = useState('');
    const [pushToken, setPushToken] = useState(null);

    // appType 확인
    const appType = localStorage.getItem('appType') || 'admin';

    // 이미 로그인된 상태로 /auth/signin 진입 시(앱에서 직접 로그인 URL로 열었을 때) 메인으로 이동
    useEffect(() => {
        const token = localStorage.getItem('accessToken');
        if (!token) return;
        try {
            const decoded = jwt.decode(token);
            if (decoded.exp && decoded.exp > Date.now() / 1000) {
                navigate('/', { replace: true });
            }
        } catch (_) { }
    }, [navigate]);

    // StatusBar 숨기기 (이 페이지에서만)
    useEffect(() => {
        if (isWebView() && window.ReactNativeWebView) {
            // 페이지 진입 시 StatusBar 숨기기
            window.ReactNativeWebView.postMessage(
                JSON.stringify({
                    type: 'setStatusBarHidden',
                    data: true,
                })
            );

            // 페이지 이탈 시 StatusBar 다시 보이기
            return () => {
                window.ReactNativeWebView.postMessage(
                    JSON.stringify({
                        type: 'setStatusBarHidden',
                        data: false,
                    })
                );
            };
        }
    }, []);

    // 푸시 토큰 가져오기
    useEffect(() => {
        if (isWebView()) {
            // 이미 설정된 토큰이 있으면 사용
            if (window.pushToken) {
                setPushToken(window.pushToken);
            }

            // pushTokenReceived 이벤트 리스너 등록
            const handlePushTokenReceived = (event) => {
                const token = event.detail || window.pushToken;
                if (token) {
                    setPushToken(token);
                }
            };

            window.addEventListener('pushTokenReceived', handlePushTokenReceived);

            // 푸시 토큰 요청
            if (window.ReactNativeWebView) {
                window.ReactNativeWebView.postMessage(
                    JSON.stringify({
                        type: 'requestPushToken',
                    })
                );
            }

            return () => {
                window.removeEventListener('pushTokenReceived', handlePushTokenReceived);
            };
        }
    }, []);

    // 이메일 유효성 검사 (utils/validation.isEmail)
    const validateEmail = email => isEmail(email);

    // 비밀번호 유효성 검사
    const validatePassword = password => {
        return password.length > 0;
    };

    // 입력값 변경 핸들러
    const handleEmailChange = event => {
        const value = event.target.value;
        setEmail(value);

        // 이메일 유효성 검사
        if (!value) {
            setEmailError(false);
            setEmailErrorMessage('');
        } else if (!validateEmail(value)) {
            setEmailError(true);
            setEmailErrorMessage(t('please_enter_a_valid_email_address'));
        } else {
            setEmailError(false);
            setEmailErrorMessage('');
        }

        // 비밀번호 유효성 검사도 함께 수행
        if (!password) {
            setPasswordError(false);
            setPasswordErrorMessage('');
        } else if (!validatePassword(password)) {
            setPasswordError(true);
            setPasswordErrorMessage(t('please_enter_password'));
        } else {
            setPasswordError(false);
            setPasswordErrorMessage('');
        }

        // 에러가 없으면 전체 에러 메시지 초기화
        if ((!value || validateEmail(value)) && (!password || validatePassword(password))) {
            setError('');
        }
    };

    const handlePasswordChange = event => {
        const value = event.target.value;
        setPassword(value);

        // 비밀번호 유효성 검사
        if (!value) {
            setPasswordError(false);
            setPasswordErrorMessage('');
        } else if (!validatePassword(value)) {
            setPasswordError(true);
            setPasswordErrorMessage(t('please_enter_password'));
        } else {
            setPasswordError(false);
            setPasswordErrorMessage('');
        }

        // 이메일 유효성 검사도 함께 수행
        if (!email) {
            setEmailError(false);
            setEmailErrorMessage('');
        } else if (!validateEmail(email)) {
            setEmailError(true);
            setEmailErrorMessage(t('please_enter_a_valid_email_address'));
        } else {
            setEmailError(false);
            setEmailErrorMessage('');
        }

        // 에러가 없으면 전체 에러 메시지 초기화
        if ((!email || validateEmail(email)) && (!value || validatePassword(value))) {
            setError('');
        }
    };

    const handleSignIn = async () => {
        // 입력값 유효성 검사
        let isValid = true;
        setError('');

        if (!email) {
            setEmailError(true);
            setEmailErrorMessage(t('please_enter_your_email'));
            isValid = false;
        } else if (!validateEmail(email)) {
            setEmailError(true);
            setEmailErrorMessage(t('please_enter_a_valid_email_address'));
            isValid = false;
        } else {
            setEmailError(false);
            setEmailErrorMessage('');
        }

        if (!password) {
            setPasswordError(true);
            setPasswordErrorMessage(t('please_enter_password'));
            isValid = false;
        } else {
            setPasswordError(false);
            setPasswordErrorMessage('');
        }

        if (!isValid) {
            return;
        }

        setIsLoading(true);

        try {
            // 푸시 토큰 준비 (React Native 환경에서만)
            let finalPushToken = null;
            if (isWebView()) {
                // 최신 토큰 확인
                if (window.pushToken) {
                    finalPushToken = window.pushToken;
                } else if (pushToken) {
                    finalPushToken = pushToken;
                }
            }

            const tokenData = {
                grant_type: 'password',
                username: email,
                password: password,
            };

            // 푸시 토큰이 있으면 추가
            if (finalPushToken) {
                tokenData.push_token = finalPushToken;
            }

            const response = await authorizeAPI.getToken(tokenData);

            if (response.access_token) {
                // 사용자 정보 가져오기
                const userResponse = await userAPI.getMyInfo(
                    response.access_token
                );

                const userGrade = userResponse.data.user?.grade || 0;

                // grade 체크
                let isGradeValid = false;
                if (appType === 'user') {
                    // user 앱: grade 1만 허용
                    isGradeValid = userGrade === 1;
                } else {
                    // admin 앱: grade 50 이상만 허용
                    isGradeValid = userGrade >= 50;
                }

                if (!isGradeValid) {
                    // 로그아웃 처리: localStorage 및 Redux 상태 초기화
                    localStorage.removeItem('accessToken');
                    localStorage.removeItem('refreshToken');
                    localStorage.removeItem('meInfo');
                    localStorage.removeItem('userListFilterType');
                    localStorage.removeItem('userListSearchName');

                    dispatch(setAuth({ accessToken: null, refreshToken: null }));

                    // WebView인 경우 로그아웃 메시지 전송
                    if (isWebView()) {
                        window.ReactNativeWebView.postMessage(
                            JSON.stringify({
                                type: 'logout',
                            })
                        );
                    }

                    setError(
                        appType === 'user'
                            ? '사용자만 로그인할 수 있습니다.'
                            : '관리자만 로그인할 수 있습니다.'
                    );
                    setEmailError(true);
                    setPasswordError(true);
                    setIsLoading(false);
                    return;
                }

                // meInfo 저장
                localStorage.setItem(
                    'meInfo',
                    JSON.stringify(userResponse.data.user)
                );

                // 토큰 저장
                localStorage.setItem('accessToken', response.access_token);
                localStorage.setItem('refreshToken', response.refresh_token);

                // Redux 상태 업데이트
                dispatch(
                    setAuth({
                        accessToken: response.access_token,
                        refreshToken: response.refresh_token,
                    })
                );

                if (isWebView()) {
                    window.ReactNativeWebView.postMessage(
                        JSON.stringify({
                            type: 'accessToken',
                            data: {
                                accessToken: response.access_token,
                                refreshToken: response.refresh_token,

                            },
                        })
                    );
                }

                // meInfo 변경 이벤트 발생
                window.dispatchEvent(new Event(' '));

                // React Native WebView 통신
                if (isWebView()) {
                    try {
                        const meInfo = JSON.parse(
                            localStorage.getItem('meInfo')
                        );
                        if (meInfo) {
                            let allSettings = [];
                            let offset = 0;
                            let page;
                            do {
                                const res = await settingAPI.getSettings({
                                    userId: meInfo.id,
                                    offset,
                                });
                                page = Array.isArray(res?.settings)
                                    ? res.settings
                                    : Array.isArray(res?.data?.settings)
                                        ? res.data.settings
                                        : [];
                                allSettings = allSettings.concat(page);
                                offset += API_PAGE_SIZE;
                            } while (page.length === API_PAGE_SIZE);
                            // settingResponse = { data: { settings: allSettings } } if needed later

                            // // 푸시 알림 설정 확인
                            // let noticePush = true;
                            // if (
                            //     settingResponse &&
                            //     settingResponse.data &&
                            //     Array.isArray(settingResponse.data.settings)
                            // ) {
                            //     const noticeSetting =
                            //         settingResponse.data.settings.find(
                            //             s => s.name === 'noticePush'
                            //         );
                            //     noticePush = !(
                            //         noticeSetting &&
                            //         (noticeSetting.value === false ||
                            //             noticeSetting.value === 'false')
                            //     );
                            // }

                            // let activePush = true;
                            // if (
                            //     settingResponse &&
                            //     settingResponse.data &&
                            //     Array.isArray(settingResponse.data.settings)
                            // ) {
                            //     const activeSetting =
                            //         settingResponse.data.settings.find(
                            //             s => s.name === 'activePush'
                            //         );
                            //     activePush = !(
                            //         activeSetting &&
                            //         (activeSetting.value === false ||
                            //             activeSetting.value === 'false')
                            //     );
                            // }

                            // if (noticePush && activePush) {
                            window.ReactNativeWebView.postMessage(
                                JSON.stringify({
                                    type: 'subscribeTopics',
                                })
                            );
                            // }
                        }
                    } catch (e) {
                        console.error(
                            'Error setting up push notifications:',
                            e
                        );
                    }
                }

                navigate(`/`, { replace: true });
            } else {
                setError(t('sign_in_failed_please_check_your_email_and_password'));
                setEmailError(true);
                setPasswordError(true);
            }
        } catch (error) {
            console.error('Login error:', error);

            // API 에러 응답에 따른 상세한 에러 메시지
            if (error.response) {
                const status = error.response.status;
                const message =
                    error.response.data?.message || error.response.data?.error;

                switch (status) {
                    case 401:
                        setError(t('email_or_password_is_incorrect'));
                        break;
                    case 403:
                        setError(t('access_denied_please_contact_your_administrator'));
                        break;
                    case 404:
                        setError(t('user_not_found'));
                        break;
                    case 500:
                        setError(t('a_server_error_occurred_please_try_again_later'));
                        break;
                    default:
                        setError(
                            message ||
                            t('sign_in_failed_please_try_again')
                        );
                }
            } else if (error.request) {
                setError(t('please_check_your_network_connection'));
            } else {
                setError(t('sign_in_failed_please_try_again'));
            }

            setEmailError(true);
            setPasswordError(true);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <StyledContainer showVersion={true}>
            {/* Main Content */}
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '50px',
                    flex: 1,
                    justifyContent: 'center',
                    width: '100%',
                    paddingTop: '32px',
                }}
            >
                {/* Logo */}
                <DidimLogo
                    width={160}
                    height={20}
                    color={theme.palette.figma.white[100]}
                />

                {/* Form */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '10px',
                        width: '100%',
                    }}
                >
                    {/* Email Field */}
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '10px',
                        }}
                    >
                        <FormLabel text={t('email')} />

                        <InputField
                            value={email}
                            onChange={handleEmailChange}
                            placeholder="didim@twohands.com"
                            type="email"
                            showClearIcon={true}
                            onClear={() => {
                                setEmail('');
                                setEmailError(false);
                                setEmailErrorMessage('');
                            }}
                            error={emailError}
                            helperText={emailErrorMessage || ' '}
                        />
                    </Box>

                    {/* Password Field */}
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '10px',
                        }}
                    >
                        <FormLabel text={t('password')} />

                        <InputField
                            value={password}
                            onChange={handlePasswordChange}
                            placeholder={t('enter_password')}
                            type="password"
                            showVisibilityIcon={true}
                            showClearIcon={false}
                            error={passwordError}
                            helperText={passwordErrorMessage || ' '}
                        />
                    </Box>

                    <Box >
                        <Typography
                            component="span"
                            onClick={() =>
                                navigate('/auth/reset-password', {
                                    state: { type: 'findPassword', email: email },
                                })
                            }
                            sx={{
                                color: theme => theme.palette.figma.white[60],
                                fontSize: '14px',
                                fontWeight: 500,
                                letterSpacing: '-0.42px',
                                textAlign: 'center',
                                textDecoration: 'underline',
                                cursor: 'pointer',
                                display: 'inline',
                                width: 'fit-content',
                                '&:hover': {
                                    color: theme => theme.palette.figma.white[80],
                                },
                            }}
                        >
                            {t('find_password')}
                        </Typography>
                    </Box>
                </Box>
            </Box>

            {/* Bottom Section */}
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '20px',
                    width: '100%',
                    maxWidth: { md: 420 },
                }}
            >
                {/* Sign In Button */}
                <Button
                    variant="normal"
                    size={'large'}
                    disabled={
                        isLoading ||
                        !email ||
                        !password ||
                        emailError ||
                        passwordError
                    }
                    onClick={handleSignIn}
                    sx={{
                        width: '100%',
                        maxWidth: { md: '420px' },
                        height: '60px',
                        minHeight: '60px',
                        maxHeight: '60px',
                        alignSelf: 'center',
                        mx: 'auto',
                    }}
                >
                    {isLoading ? t('signing_in') : t('next')}
                </Button>

                {/* 기관가입신청 링크 */}
                {appType !== 'user' && (
                    <Typography
                        component="span"
                        onClick={() => navigate('/auth/organization-apply')}
                        sx={{
                            color: theme => theme.palette.figma.Red_didim,
                            fontSize: '14px',
                            fontWeight: 500,
                            letterSpacing: '-0.42px',
                            textAlign: 'center',
                            textDecoration: 'underline',
                            cursor: 'pointer',
                            display: 'inline',
                            width: 'fit-content',
                            '&:hover': {
                                color: theme => theme.palette.figma.white[80],
                            },
                        }}
                    >
                        {t('organization_registration')}
                    </Typography>
                )}

                {/* Error Message */}
                {error && (
                    <Typography
                        sx={{
                            color: theme => theme.palette.error.light,
                            fontSize: '14px',
                            textAlign: 'center',
                        }}
                    >
                        {error}
                    </Typography>
                )}

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '10px',
                        alignSelf: 'stretch',
                        minHeight: '136px',
                    }}
                >
                    {/* Social Login Provider */}
                    <SocialLoginProvider />

                    {/* Sign Up Link */}
                    {appType === 'user' && <SignUpLink
                        onSignUpClick={() => navigate('/auth/signup')}
                    />}
                </Box>
            </Box>
        </StyledContainer>
    );
};

export default SignIn;
